import * as React from 'react'
import { useCallback } from 'react'
import { twMerge } from 'tailwind-merge'
import { Copy, CopyCheck, PenLine, Save, ThumbsUp, ThumbsDown, ChevronUp, RotateCw } from 'lucide-react'
import { ActionPopover, Tooltip } from '@highlight/ui'
import { useSummaryById, useSummaryDate } from '@renderer/chat/hooks'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useSummaryContext } from '../hooks/use-summary-context'
import { useSummaryEdit } from '../hooks/use-summary-edit'
import { useSummaryActions } from '../hooks/use-summary-actions'

interface RegenerateActionProps {
  isGenerating?: boolean
  onRegenerate?: (e: React.MouseEvent) => void
}

export function RegenerateAction({ isGenerating = false, onRegenerate }: RegenerateActionProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onRegenerate?.(e)
  }

  return (
    <Tooltip>
      <Tooltip.Trigger render={<div className="group relative flex" />}>
        <ActionPopover.Button
          aria-label={isGenerating ? 'Refreshing summary, please wait' : 'Regenerate summary'}
          loading={isGenerating}
          onClick={handleClick}
          shape="circle"
        >
          <RotateCw />
        </ActionPopover.Button>
      </Tooltip.Trigger>
      <Tooltip.Content side="bottom">
        {isGenerating ? 'Refreshing summary, please wait' : 'Regenerate summary'}
      </Tooltip.Content>
    </Tooltip>
  )
}

interface EditActionProps {
  isEditing?: boolean
  onClick?: (e: React.MouseEvent) => void
}

export function EditAction({ isEditing = false, onClick }: EditActionProps) {
  return (
    <Tooltip>
      <Tooltip.Trigger render={<div className="group relative flex" />}>
        <ActionPopover.Button aria-label={isEditing ? 'Save edits' : 'Edit summary'} onClick={onClick} shape="circle">
          {isEditing ? <Save size={16} /> : <PenLine size={16} />}
        </ActionPopover.Button>
      </Tooltip.Trigger>
      <Tooltip.Content side="bottom">{isEditing ? 'Save edits' : 'Edit summary'}</Tooltip.Content>
    </Tooltip>
  )
}

interface CopyActionProps {
  isCopied?: boolean
  onCopy?: (e: React.MouseEvent) => void
}

export function CopyAction({ isCopied = false, onCopy }: CopyActionProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onCopy?.(e as React.MouseEvent)
  }

  return (
    <Tooltip>
      <Tooltip.Trigger render={<div className="group relative flex" />}>
        <ActionPopover.Button
          aria-label="Copy to clipboard"
          onClick={handleClick}
          className={twMerge(isCopied && 'opacity-100')}
          shape="circle"
        >
          {isCopied ? <CopyCheck size={16} /> : <Copy size={16} />}
        </ActionPopover.Button>
      </Tooltip.Trigger>
      <Tooltip.Content side="bottom">Copy to clipboard</Tooltip.Content>
    </Tooltip>
  )
}

interface EditingActionsProps {
  isSaving?: boolean
  onCancel?: (e: React.MouseEvent) => void
  onSave?: (e: React.MouseEvent) => void
}

export function EditingActions({ isSaving = false, onCancel, onSave }: EditingActionsProps) {
  return (
    <>
      <ActionPopover.Button onClick={onCancel}>Cancel</ActionPopover.Button>
      <ActionPopover.Button variant={'primary'} tone="success" disabled={isSaving} onClick={onSave}>
        Save
      </ActionPopover.Button>
    </>
  )
}

interface PositiveFeedbackActionProps {
  onPositiveFeedback?: (e: React.MouseEvent) => void
}

export function PositiveFeedbackAction({ onPositiveFeedback }: PositiveFeedbackActionProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onPositiveFeedback?.(e as React.MouseEvent)
  }

  return (
    <Tooltip>
      <Tooltip.Trigger render={<div className="group relative flex" />}>
        <ActionPopover.Button
          aria-label="This was helpful"
          variant={'ghost-neutral'}
          size={'sm'}
          className={'px-2'}
          onClick={handleClick}
        >
          <ThumbsUp size={16} />
        </ActionPopover.Button>
      </Tooltip.Trigger>
      <Tooltip.Content>This was helpful</Tooltip.Content>
    </Tooltip>
  )
}

interface NegativeFeedbackActionProps {
  onNegativeFeedback?: (e: React.MouseEvent) => void
}

export function NegativeFeedbackAction({ onNegativeFeedback }: NegativeFeedbackActionProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onNegativeFeedback?.(e as React.MouseEvent)
  }

  return (
    <Tooltip>
      <Tooltip.Trigger render={<div className="group relative flex" />}>
        <ActionPopover.Button
          aria-label="This was not helpful"
          variant={'ghost-neutral'}
          size={'sm'}
          className={'px-2'}
          onClick={handleClick}
        >
          <ThumbsDown size={16} />
        </ActionPopover.Button>
      </Tooltip.Trigger>
      <Tooltip.Content>This was not helpful</Tooltip.Content>
    </Tooltip>
  )
}

interface FeedbackActionsProps extends React.ComponentProps<'div'> {
  onPositiveFeedback?: (e: React.MouseEvent) => void
  onNegativeFeedback?: (e: React.MouseEvent) => void
}

export function FeedbackActions({ className, onPositiveFeedback, onNegativeFeedback, ...props }: FeedbackActionsProps) {
  return (
    <div className={twMerge('text-light/20 font-350 flex items-center gap-2 text-sm', className)} {...props}>
      Was this helpful?
      <div className={'flex items-center gap-1'}>
        <ThumbsUp
          size={20}
          className={
            'text-light hover:text-green cursor-pointer opacity-20 transition-[color,opacity] hover:opacity-60'
          }
          onClick={onPositiveFeedback}
        />
        <ThumbsDown
          size={20}
          className={'text-light hover:text-red cursor-pointer opacity-20 transition-[color,opacity] hover:opacity-60'}
          onClick={onNegativeFeedback}
        />
      </div>
    </div>
  )
}

interface ExpandActionProps {
  isExpanded?: boolean
  onToggleExpand?: (e: React.MouseEvent) => void
}

export function ExpandAction({ isExpanded = false, onToggleExpand }: ExpandActionProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onToggleExpand?.(e as React.MouseEvent)
  }

  return (
    <Tooltip>
      <Tooltip.Trigger render={<div className="group relative flex" />}>
        <ActionPopover.Button
          aria-label={isExpanded ? 'Collapse summary' : 'Expand summary'}
          onClick={handleClick}
          shape="circle"
        >
          <ChevronUp size={16} className={twMerge('transition-transform', isExpanded ? 'rotate-0' : 'rotate-180')} />
        </ActionPopover.Button>
      </Tooltip.Trigger>
      <Tooltip.Content side="bottom">{isExpanded ? 'Collapse summary' : 'Expand summary'}</Tooltip.Content>
    </Tooltip>
  )
}

export function SummaryActionsBar() {
  const { summary, isExpanded, setExpanded } = useSummaryContext()
  const { data: fullSummary, isFetching } = useSummaryById(summary.id)
  const { isToday, dayLabel } = useSummaryDate(summary)
  const { isEditing, setEdited, setEditing, onSaveOrEdit, isSaving } = useSummaryEdit()
  const { isGenerating, isCopied, onGenerate, onCopy } = useSummaryActions()

  const onCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditing(false)
    setEdited(undefined)
  }

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      const newExpanded = !isExpanded
      setExpanded(newExpanded)
      AmplitudeRenderer.trackEvent('Summary Action', {
        type: newExpanded ? 'Expand' : 'Collapse',
        timePeriod: isToday ? 'Today' : dayLabel,
      })
    },
    [setExpanded, isExpanded, isToday, dayLabel],
  )

  // Show if there's a summary OR if currently generating/fetching
  // This ensures the bar stays visible during regeneration and data refetch
  const hasContent = fullSummary?.summary || isGenerating || isFetching
  if (!hasContent) {
    return null
  }

  return (
    <ActionPopover.Body mode="popLayout">
      <ActionPopover.Screen id={summary.id} key="editing" isActive={isEditing} className="flex items-center gap-1">
        <EditingActions isSaving={isSaving} onCancel={onCancelEdit} onSave={onSaveOrEdit} />
      </ActionPopover.Screen>

      <ActionPopover.Screen id={summary.id} key="actions" isActive={!isEditing} className="flex items-center gap-1">
        {isToday && <RegenerateAction isGenerating={isGenerating} onRegenerate={onGenerate} />}
        <EditAction isEditing={isEditing} onClick={onSaveOrEdit} />
        <CopyAction isCopied={isCopied} onCopy={onCopy} />
        <ExpandAction isExpanded={isExpanded} onToggleExpand={handleToggleExpand} />
      </ActionPopover.Screen>
    </ActionPopover.Body>
  )
}
